import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import styles from './ExploreTaglioRow.module.scss';

const TaglioPreviewCarousel = ({ slides, resetKey }) => {
  const { t } = useTranslation();
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(slides.length <= 1);
  const swiperKey = `${resetKey}-${slides.length}`;
  const hasMultiple = slides.length > 1;

  useEffect(() => {
    if (!swiper) return undefined;

    const sync = () => {
      setActiveIndex(swiper.activeIndex);
      setAtStart(swiper.isBeginning);
      setAtEnd(swiper.isEnd);
    };

    sync();
    swiper.on('slideChange', sync);

    return () => {
      swiper.off('slideChange', sync);
    };
  }, [swiper]);

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(0, 0);
      setActiveIndex(0);
      setAtStart(true);
      setAtEnd(slides.length <= 1);
    }
  }, [swiperKey, swiper, slides.length]);

  if (!slides.length) {
    return <div className={styles.preview_placeholder} />;
  }

  return (
    <div className={styles.carousel_wrap}>
      <div className={styles.carousel_stage}>
        <Swiper
          key={swiperKey}
          className={styles.swiper}
          slidesPerView={1}
          spaceBetween={0}
          speed={320}
          onSwiper={setSwiper}
          allowTouchMove={hasMultiple}
        >
          {slides.map((url, index) => (
            <SwiperSlide key={`${url}-${index}`} className={styles.swiper_slide}>
              <img src={url} alt="" className={styles.preview_img} draggable={false} />
            </SwiperSlide>
          ))}
        </Swiper>

        {hasMultiple ? (
          <>
            <button
              type="button"
              className={`${styles.carousel_nav} ${styles.carousel_nav_prev}`}
              onClick={() => swiper?.slidePrev()}
              disabled={atStart}
              aria-label={t('scheduler.taskInfoModal.exploreHaircutPrev')}
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              className={`${styles.carousel_nav} ${styles.carousel_nav_next}`}
              onClick={() => swiper?.slideNext()}
              disabled={atEnd}
              aria-label={t('scheduler.taskInfoModal.exploreHaircutNext')}
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className={styles.carousel_dots} role="tablist" aria-label={t('scheduler.taskInfoModal.exploreHaircutPreview')}>
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              className={clsx(styles.carousel_dot, index === activeIndex && styles.carousel_dot_active)}
              onClick={() => swiper?.slideTo(index)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TaglioPreviewCarousel;
